import SEO from "@/components/SEO";
import { SeoHead } from '@/components/SeoHead';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Server, Shield, Zap, Headphones, ChevronRight, Star } from "lucide-react";

export default function WebHosting() {
  const { openDrawer } = useQuoteDrawer();

  const features = [
    {
      title: "High-Performance Servers",
      description: "SSD storage, optimized configurations, and global CDN for maximum speed.",
      icon: <Server className="h-8 w-8 text-blue-400" />
    },
    {
      title: "Advanced Security",
      description: "SSL certificates, DDoS protection, malware scanning, and regular backups.",
      icon: <Shield className="h-8 w-8 text-green-400" />
    },
    {
      title: "99.9% Uptime Guarantee",
      description: "Reliable hosting infrastructure with redundancy and monitoring systems.",
      icon: <Zap className="h-8 w-8 text-yellow-400" />
    },
    {
      title: "24/7 Expert Support",
      description: "Round-the-clock technical support from hosting specialists.",
      icon: <Headphones className="h-8 w-8 text-purple-400" />
    }
  ];

  const plans = [
    {
      name: "Shared Hosting",
      price: "$9.99",
      period: "per month",
      features: ["10GB SSD Storage", "Unlimited Bandwidth", "Free SSL", "24/7 Support"],
      color: "from-blue-500 to-blue-600"
    },
    {
      name: "VPS Hosting",
      price: "$29.99",
      period: "per month",
      features: ["50GB SSD Storage", "Dedicated Resources", "Root Access", "Advanced Security"],
      popular: true,
      color: "from-purple-500 to-purple-600"
    },
    {
      name: "Dedicated Server",
      price: "$99.99",
      period: "per month",
      features: ["500GB SSD Storage", "Full Server Control", "Custom Config", "Priority Support"],
      color: "from-amber-500 to-amber-600"
    }
  ];

  return (
    <>
      <SeoHead 
        title="Premium Web Hosting UAE | ZeeX Soft"
        description="Blazing-fast, secure web hosting in Dubai & Sharjah. 99.9% uptime with 24/7 expert support."
        keywords="web hosting uae, website hosting dubai, cloud hosting sharjah"
      />

      <main>
  

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 to-gray-800 py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 mb-6">
                <Server className="h-5 w-5 mr-2 text-white" />
                <span className="text-sm font-medium text-white">Web Hosting</span>
              </div>
              <h1 className="font-orbitron font-bold text-5xl md:text-6xl text-white mb-6">
                Powerful <span className="gradient-text">Web Hosting</span>
              </h1>
              <p className="text-xl text-gray-300 mb-10">
              High-performance hosting solutions for businesses in UAE
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  onClick={openDrawer}
                >
                  Get Hosting Quote
                </Button>
                <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
                  Compare Plans
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Hosting Features</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">Everything you need for a successful online presence</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-800 rounded-xl p-8 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Hosting Plans */}
        <section className="py-20 bg-gray-800">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Hosting Plans</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">Choose the perfect solution for your website needs</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className={`bg-gray-700 rounded-xl p-8 relative hover:shadow-lg transition-all ${plan.popular ? 'ring-2 ring-purple-500' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <h3 className="font-orbitron font-semibold text-2xl mb-4 text-white">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${plan.color}">{plan.price}</span>
                      <span className="text-gray-400 ml-2">{plan.period}</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-gradient-to-r ${plan.color} rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full bg-gradient-to-r ${plan.color} hover:opacity-90`}
                    onClick={openDrawer}
                  >
                    Choose Plan
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-500 to-blue-600">
          <div className="container mx-auto px-6 text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-white mb-6">Ready to Launch Your Website?</h2>
              <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
                Get started with our reliable hosting solutions today
              </p>
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 hover:text-blue-700 font-medium"
                onClick={openDrawer}
              >
                Get Started Now
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
